import i18n from '@/i18n';
import { resolveIntlLocale } from '@/lib/i18nLocale';

type DateInput = string | Date | null | undefined;

function currentLocale(locale?: string | null): string {
	return resolveIntlLocale(locale ?? i18n?.language ?? 'en');
}

export function resolveDisplayTimeZone(): string | undefined {
	try {
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return typeof timeZone === 'string' && timeZone.trim() ? timeZone : undefined;
	} catch {
		return undefined;
	}
}

function withDisplayTimeZone(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions {
	if (options.timeZone) return options;
	const timeZone = resolveDisplayTimeZone();
	return timeZone ? { ...options, timeZone } : options;
}

/** 달력 일자만(시간 없음): `formatLocaleDateTime`의 날짜 부분과 동일한 규칙 */
export const localeFormatOptionsDateOnly: Intl.DateTimeFormatOptions = {
	year: 'numeric',
	month: 'short',
	day: 'numeric',
};

/** 시·분만: 24시각 두 자리 (예: 00:00, 09:05) */
export const localeFormatOptionsTimeHm24: Intl.DateTimeFormatOptions = {
	hour: '2-digit',
	minute: '2-digit',
	hour12: false,
};

/** 날짜 + 시·분: 날짜는 로케일 관례, 시각은 24시각 HH:mm */
export const localeFormatOptionsDateTimeHm24: Intl.DateTimeFormatOptions = {
	...localeFormatOptionsDateOnly,
	...localeFormatOptionsTimeHm24,
};

/** 날짜 + 시·분·초: 시각은 24시각 */
export const localeFormatOptionsDateTimeHms24: Intl.DateTimeFormatOptions = {
	...localeFormatOptionsDateOnly,
	hour: '2-digit',
	minute: '2-digit',
	second: '2-digit',
	hour12: false,
};

/**
 * 비표준 구분자를 ISO에 가깝게 정규화해 `new Date()` 파싱 실패(화면 '-')를 막음.
 * - "YYYY-MM-DD HH:mm:ss" → T 구분
 * - "YYYY-MM-DD-HH:mm:ss" → T 구분
 */
export function normalizeDateString(raw: string): string {
	const t = raw.trim();
	if (!t || t === 'null' || t === 'undefined') return t;
	if (/^\d{4}-\d{2}-\d{2}-\d{2}:\d{2}/.test(t)) {
		return t.replace(/^(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2}(:\d{2})?(?:\.\d+)?)/, '$1T$2');
	}
	return t.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}(:\d{2})?(?:\.\d+)?)/, '$1T$2');
}

function parseDateInput(value: DateInput, dateOnlyAsLocal = false): Date | null {
	if (value == null) return null;
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value;
	}
	const s = String(value).trim();
	if (!s) return null;
	const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (dateOnly && dateOnlyAsLocal) {
		const [, y, m, d] = dateOnly;
		return new Date(Number(y), Number(m) - 1, Number(d));
	}
	const parsed = new Date(normalizeDateString(s));
	return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatLocaleDate(
	value: DateInput,
	locale?: string | null,
	options?: Intl.DateTimeFormatOptions
): string {
	const date = parseDateInput(value, true);
	if (!date) return '-';
	return new Intl.DateTimeFormat(currentLocale(locale), withDisplayTimeZone(options ?? { dateStyle: 'medium' })).format(date);
}

export function formatLocaleDateShort(value: DateInput, locale?: string | null): string {
	const date = parseDateInput(value, true);
	if (!date) return '-';
	return new Intl.DateTimeFormat(currentLocale(locale), withDisplayTimeZone(localeFormatOptionsDateOnly)).format(date);
}

export function formatLocaleTime(
	value: DateInput,
	locale?: string | null,
	options?: Intl.DateTimeFormatOptions
): string {
	const date = parseDateInput(value);
	if (!date) return '-';
	return new Intl.DateTimeFormat(currentLocale(locale), withDisplayTimeZone(options ?? localeFormatOptionsTimeHm24)).format(date);
}

export function formatLocaleDateTime(
	value: DateInput,
	locale?: string | null,
	options?: Intl.DateTimeFormatOptions
): string {
	const date = parseDateInput(value);
	if (!date) return '-';
	return new Intl.DateTimeFormat(currentLocale(locale), withDisplayTimeZone(options ?? localeFormatOptionsDateTimeHm24)).format(date);
}

export function formatLocaleNumber(
	value: number | null | undefined,
	locale?: string | null,
	options?: Intl.NumberFormatOptions
): string {
	if (value == null || !Number.isFinite(value)) return '-';
	return new Intl.NumberFormat(currentLocale(locale), options).format(value);
}

export function formatLocaleInteger(value: number | null | undefined, locale?: string | null): string {
	return formatLocaleNumber(value, locale, { maximumFractionDigits: 0 });
}

export function formatLocalePercent(
	value: number | null | undefined,
	locale?: string | null,
	options?: Intl.NumberFormatOptions
): string {
	return formatLocaleNumber(value, locale, {
		style: 'percent',
		maximumFractionDigits: 1,
		...options,
	});
}
